import React, { useMemo, useCallback, useContext, useState, useEffect, Fragment, useRef } from 'react';
import { CurtainState, DeviceColors, DeviceType, EvaporativeCoolerPower, SednaInfo } from 'app/model/Device'
import { useLocalizedStrings, LocalizationContext } from 'app/localization/Context';
import { WebViewManagerContext } from 'app/home/general-components/WebViewManagerContext';

import ScenarioSwitchIcon from 'app/home/home-components/scenario-switch-icon/ScenarioSwitchIcon';
import Popup, { usePopup } from 'app/home/general-components/Popup/Popup';
import RoomHeaderTab from 'app/home/home-components/room-header-tab/RoomHeaderTab';
import { GatewayDataContext } from 'app/home/Home';
import { AudioControllerContents } from './AudioControllerContents';
import useWindowDimensions from 'app/home/general-components/WindowDimensions';
import { AddDeviceStatus } from 'app/home/overlay-pages/device/add-device/AddDevice';
import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';
import { WCButton, WCScenarioTag } from 'app/home/overlay-pages/device/deivce-detail/device-control/WCControl';
import ThermostatButton from './ThermostatButton';
import { CoolerButton } from 'app/home/overlay-pages/device/deivce-detail/device-control/CoolerControl';
import { DoorbellSingleButton, DoorbellStatePanel } from 'app/home/overlay-pages/device/deivce-detail/device-control/DoorbellControl';
import { ElevatorSingleButton, ElevatorStatePanel } from 'app/home/overlay-pages/device/deivce-detail/device-control/ElevatorControl';

import { withRandomIds } from 'images/withRandomIds';

import { ReactComponent as _IconLampOn1 } from 'images/device/status-icon/lamp-on-1.svg';
import { ReactComponent as _IconLampOn2 } from 'images/device/status-icon/lamp-on-2.svg';
import { ReactComponent as _IconLampOn3 } from 'images/device/status-icon/lamp-on-3.svg';
import { ReactComponent as _IconLampOn4 } from 'images/device/status-icon/lamp-on-4.svg';
import { ReactComponent as _IconLampOff1 } from 'images/device/status-icon/lamp-off-1.svg';
import { ReactComponent as _IconLampOff2 } from 'images/device/status-icon/lamp-off-2.svg';
import { ReactComponent as _IconLampOff3 } from 'images/device/status-icon/lamp-off-3.svg';
import { ReactComponent as _IconLampOff4 } from 'images/device/status-icon/lamp-off-4.svg';

import { ReactComponent as _IconRelayOn1 } from 'images/device/status-icon/relay-on-1.svg';
import { ReactComponent as _IconRelayOn2 } from 'images/device/status-icon/relay-on-2.svg';
import { ReactComponent as _IconRelayOn3 } from 'images/device/status-icon/relay-on-3.svg';
import { ReactComponent as _IconRelayOn4 } from 'images/device/status-icon/relay-on-4.svg';
import { ReactComponent as _IconRelayOff1 } from 'images/device/status-icon/relay-off-1.svg';
import { ReactComponent as _IconRelayOff2 } from 'images/device/status-icon/relay-off-2.svg';
import { ReactComponent as _IconRelayOff3 } from 'images/device/status-icon/relay-off-3.svg';
import { ReactComponent as _IconRelayOff4 } from 'images/device/status-icon/relay-off-4.svg';

import { ReactComponent as _IconPlugOn } from 'images/device/status-icon/plug-on.svg';
import { ReactComponent as _IconPlugOff } from 'images/device/status-icon/plug-off.svg';
import { ReactComponent as _IconDoorOpen } from 'images/device/status-icon/door-open.svg';
import { ReactComponent as _IconDoorClose } from 'images/device/status-icon/door-close.svg';
import { ReactComponent as _IconSirenOn } from 'images/device/status-icon/siren-on.svg';
import { ReactComponent as _IconSirenOnWhite } from 'images/device/status-icon/siren-on-white.svg';
import { ReactComponent as _IconSirenOff } from 'images/device/status-icon/siren-off.svg';

import { ReactComponent as _IconX } from 'images/general/x.svg';
import { ReactComponent as _IconTickCircle } from 'images/general/tick-circle.svg';
import { ReactComponent as _IconPlus } from 'images/general/icon-add.svg';
import { ReactComponent as _IconMore } from 'images/general/icon-more.svg';
import { ReactComponent as _IconMoreDark } from 'images/general/icon-more-dark.svg';
import { ReactComponent as _IconDisconnect } from 'images/general/disconnect.svg';
import { ReactComponent as _IconSelection } from 'images/general/icon-badge-select.svg';
import { ReactComponent as _IconIndicate } from 'images/general/indicate.svg';
import { ReactComponent as _IconMute } from 'images/general/mute-gray.svg';
import { ReactComponent as _IconDeleteWhite } from 'images/general/delete-white.svg';
import { ReactComponent as _IconDeleteRed } from 'images/general/delete-red.svg';

import { ReactComponent as _IconMotionDetected } from 'images/device/status-icon/motion-detected.svg';
import { ReactComponent as _IconMotionStandby } from 'images/device/status-icon/motion-standby.svg';
import { ReactComponent as _IconSoundDetected } from 'images/device/status-icon/sound-detected.svg';
import { ReactComponent as _IconSoundMute } from 'images/device/status-icon/sound-mute.svg';
import { ReactComponent as _IconLightBright } from 'images/device/status-icon/light-bright.svg';
import { ReactComponent as _IconLightNormal } from 'images/device/status-icon/light-normal.svg';
import { ReactComponent as _IconLightOff } from 'images/device/status-icon/light-off.svg';
import { ReactComponent as _IconTemperature1 } from 'images/device/status-icon/temperature-1.svg';
import { ReactComponent as _IconTemperature2 } from 'images/device/status-icon/temperature-2.svg';
import { ReactComponent as _IconTemperature3 } from 'images/device/status-icon/temperature-3.svg';
import { ReactComponent as _IconTemperature4 } from 'images/device/status-icon/temperature-4.svg';

// eslint-disable-next-line
import { ReactComponent as _IconStatusMotionDetectedAr } from 'images/device/status-text-icon/motion-detected-ar.svg';
// eslint-disable-next-line
import { ReactComponent as _IconStatusDetectedAr } from 'images/device/status-text-icon/motion-detected-ar.svg';

import { ReactComponent as _IconStatusMotionDetectedFa } from 'images/device/status-text-icon/motion-detected-fa.svg';
import { ReactComponent as _IconStatusMotionDetected } from 'images/device/status-text-icon/motion-detected.svg';
import { ReactComponent as _IconStatusSoundDetectedAr } from 'images/device/status-text-icon/sound-detected-ar.svg';
import { ReactComponent as _IconStatusSoundDetectedFa } from 'images/device/status-text-icon/sound-detected-fa.svg';
import { ReactComponent as _IconStatusSoundDetected } from 'images/device/status-text-icon/sound-detected.svg';

import { ReactComponent as _IconThreeDotsLoadingWhite } from 'images/general/three-dots-loading-white.svg';
import { ReactComponent as _IconThreeDotsLoadingGray } from 'images/general/three-dots-loading-gray.svg';
import { ReactComponent as _IconThreeDotsLoadingDark } from 'images/general/three-dots-loading-dark.svg';
import { ReactComponent as _IconThreeDotsLoadingGradient } from 'images/general/three-dots-loading-gradient.svg';

import { ReactComponent as _IconStatusON } from 'images/device/status-text-icon/on.svg';
import { ReactComponent as _IconStatusOpen } from 'images/device/status-text-icon/open.svg';
import { ReactComponent as _IconStatusDetected } from 'images/device/status-text-icon/detected.svg';
import { ReactComponent as _IconStatusONAr } from 'images/device/status-text-icon/on-ar.svg';
import { ReactComponent as _IconStatusOpenAr } from 'images/device/status-text-icon/open-ar.svg';
import { ReactComponent as _IconStatusONFa } from 'images/device/status-text-icon/on-fa.svg';
import { ReactComponent as _IconStatusOpenFa } from 'images/device/status-text-icon/open-fa.svg';
import { ReactComponent as _IconStatusDetectedFa } from 'images/device/status-text-icon/detected-fa.svg';

import { ReactComponent as _IconRemoteAc } from 'images/device/symbol/hinava_gradient/remote-ac.svg';
import { ReactComponent as _IconRemoteFan } from 'images/device/symbol/hinava_gradient/remote-fan.svg';
import { ReactComponent as _IconRemoteHomeTheater } from 'images/device/symbol/hinava_gradient/remote-home-theater.svg';
import { ReactComponent as _IconRemoteProjector } from 'images/device/symbol/hinava_gradient/remote-projector.svg';
import { ReactComponent as _IconRemoteReciever } from 'images/device/symbol/hinava_gradient/remote-reciever.svg';
import { ReactComponent as _IconRemoteSoundbar } from 'images/device/symbol/hinava_gradient/remote-soundbar.svg';
import { ReactComponent as _IconRemoteTv } from 'images/device/symbol/hinava_gradient/remote-tv.svg';
import { ReactComponent as _IconRemote } from 'images/device/symbol/hinava_gradient/remote.svg';
import { ReactComponent as _IconCurtainSymbol } from 'images/device/symbol/hinava_gradient/curtain.svg';

import { ReactComponent as _IconCurtainClose1 } from 'images/device/status-icon/curtain-close-1.svg';
import { ReactComponent as _IconCurtainClose2 } from 'images/device/status-icon/curtain-close-2.svg';
import { ReactComponent as _IconCurtainOpen1 } from 'images/device/status-icon/curtain-open-1.svg';
import { ReactComponent as _IconCurtainOpen2 } from 'images/device/status-icon/curtain-open-2.svg';
import { ReactComponent as _IconCurtainCloseDark1 } from 'images/device/status-icon/curtain-close-dark-1.svg';
import { ReactComponent as _IconCurtainCloseDark2 } from 'images/device/status-icon/curtain-close-dark-2.svg';
import { ReactComponent as _IconCurtainOpenDark1 } from 'images/device/status-icon/curtain-open-dark-1.svg';
import { ReactComponent as _IconCurtainOpenDark2 } from 'images/device/status-icon/curtain-open-dark-2.svg';

import { ReactComponent as _IconDevicesLampNoChange1 } from 'images/device/status-icon/lamp-no-change-1.svg';
import { ReactComponent as _IconDevicesLampNoChange2 } from 'images/device/status-icon/lamp-no-change-2.svg';
import { ReactComponent as _IconDevicesLampNoChange3 } from 'images/device/status-icon/lamp-no-change-3.svg';
import { ReactComponent as _IconDevicesLampNoChange4 } from 'images/device/status-icon/lamp-no-change-4.svg';
import { ReactComponent as _IconDevicesPlugNoChange } from 'images/device/status-icon/plug-no-change.svg';
import { ReactComponent as _IconDevicesRelayNoChange1 } from 'images/device/status-icon/relay-no-change-1.svg';
import { ReactComponent as _IconDevicesRelayNoChange2 } from 'images/device/status-icon/relay-no-change-2.svg';
import { ReactComponent as _IconDevicesRelayNoChange3 } from 'images/device/status-icon/relay-no-change-3.svg';
import { ReactComponent as _IconDevicesRelayNoChange4 } from 'images/device/status-icon/relay-no-change-4.svg';

import { ReactComponent as _IconDevicesStatusNoChangeAr } from 'images/device/status-text-icon/no-change-ar.svg';
import { ReactComponent as _IconDevicesStatusNoChangeFa } from 'images/device/status-text-icon/no-change-fa.svg';
import { ReactComponent as _IconDevicesStatusNoChange } from 'images/device/status-text-icon/no-change.svg';

import './Devices.scss'

const IconLampOn1 = withRandomIds(_IconLampOn1);
const IconLampOn2 = withRandomIds(_IconLampOn2);
const IconLampOn3 = withRandomIds(_IconLampOn3);
const IconLampOn4 = withRandomIds(_IconLampOn4);
const IconLampOff1 = withRandomIds(_IconLampOff1);
const IconLampOff2 = withRandomIds(_IconLampOff2);
const IconLampOff3 = withRandomIds(_IconLampOff3);
const IconLampOff4 = withRandomIds(_IconLampOff4);
const IconRelayOn1 = withRandomIds(_IconRelayOn1);
const IconRelayOn2 = withRandomIds(_IconRelayOn2);
const IconRelayOn3 = withRandomIds(_IconRelayOn3);
const IconRelayOn4 = withRandomIds(_IconRelayOn4);
const IconRelayOff1 = withRandomIds(_IconRelayOff1);
const IconRelayOff2 = withRandomIds(_IconRelayOff2);
const IconRelayOff3 = withRandomIds(_IconRelayOff3);
const IconRelayOff4 = withRandomIds(_IconRelayOff4);
const IconPlugOn = withRandomIds(_IconPlugOn);
const IconPlugOff = withRandomIds(_IconPlugOff);
const IconDoorOpen = withRandomIds(_IconDoorOpen);
const IconDoorClose = withRandomIds(_IconDoorClose);
const IconSirenOn = withRandomIds(_IconSirenOn);
const IconSirenOnWhite = withRandomIds(_IconSirenOnWhite);
const IconSirenOff = withRandomIds(_IconSirenOff);
const IconX = withRandomIds(_IconX);
const IconTickCircle = withRandomIds(_IconTickCircle);
const IconPlus = withRandomIds(_IconPlus);
const IconMore = withRandomIds(_IconMore);
const IconMoreDark = withRandomIds(_IconMoreDark);
const IconDisconnect = withRandomIds(_IconDisconnect);
const IconSelection = withRandomIds(_IconSelection);
const IconIndicate = withRandomIds(_IconIndicate);
const IconMute = withRandomIds(_IconMute);
const IconDeleteWhite = withRandomIds(_IconDeleteWhite);
const IconDeleteRed = withRandomIds(_IconDeleteRed);
const IconMotionDetected = withRandomIds(_IconMotionDetected);
const IconMotionStandby = withRandomIds(_IconMotionStandby);
const IconSoundDetected = withRandomIds(_IconSoundDetected);
const IconSoundMute = withRandomIds(_IconSoundMute);
const IconLightBright = withRandomIds(_IconLightBright);
const IconLightNormal = withRandomIds(_IconLightNormal);
const IconLightOff = withRandomIds(_IconLightOff);
const IconTemperature1 = withRandomIds(_IconTemperature1);
const IconTemperature2 = withRandomIds(_IconTemperature2);
const IconTemperature3 = withRandomIds(_IconTemperature3);
const IconTemperature4 = withRandomIds(_IconTemperature4);
const IconStatusMotionDetectedAr = withRandomIds(_IconStatusMotionDetectedAr);
const IconStatusDetectedAr = withRandomIds(_IconStatusDetectedAr);
const IconStatusMotionDetectedFa = withRandomIds(_IconStatusMotionDetectedFa);
const IconStatusMotionDetected = withRandomIds(_IconStatusMotionDetected);
const IconStatusSoundDetectedAr = withRandomIds(_IconStatusSoundDetectedAr);
const IconStatusSoundDetectedFa = withRandomIds(_IconStatusSoundDetectedFa);
const IconStatusSoundDetected = withRandomIds(_IconStatusSoundDetected);
const IconThreeDotsLoadingWhite = withRandomIds(_IconThreeDotsLoadingWhite);
const IconThreeDotsLoadingGray = withRandomIds(_IconThreeDotsLoadingGray);
const IconThreeDotsLoadingDark = withRandomIds(_IconThreeDotsLoadingDark);
const IconThreeDotsLoadingGradient = withRandomIds(_IconThreeDotsLoadingGradient);
const IconStatusON = withRandomIds(_IconStatusON);
const IconStatusOpen = withRandomIds(_IconStatusOpen);
const IconStatusDetected = withRandomIds(_IconStatusDetected);
const IconStatusONAr = withRandomIds(_IconStatusONAr);
const IconStatusOpenAr = withRandomIds(_IconStatusOpenAr);
const IconStatusONFa = withRandomIds(_IconStatusONFa);
const IconStatusOpenFa = withRandomIds(_IconStatusOpenFa);
const IconStatusDetectedFa = withRandomIds(_IconStatusDetectedFa);
const IconRemoteAc = withRandomIds(_IconRemoteAc);
const IconRemoteFan = withRandomIds(_IconRemoteFan);
const IconRemoteHomeTheater = withRandomIds(_IconRemoteHomeTheater);
const IconRemoteProjector = withRandomIds(_IconRemoteProjector);
const IconRemoteReciever = withRandomIds(_IconRemoteReciever);
const IconRemoteSoundbar = withRandomIds(_IconRemoteSoundbar);
const IconRemoteTv = withRandomIds(_IconRemoteTv);
const IconRemote = withRandomIds(_IconRemote);
const IconCurtainSymbol = withRandomIds(_IconCurtainSymbol);
const IconCurtainClose1 = withRandomIds(_IconCurtainClose1);
const IconCurtainClose2 = withRandomIds(_IconCurtainClose2);
const IconCurtainOpen1 = withRandomIds(_IconCurtainOpen1);
const IconCurtainOpen2 = withRandomIds(_IconCurtainOpen2);
const IconCurtainCloseDark1 = withRandomIds(_IconCurtainCloseDark1);
const IconCurtainCloseDark2 = withRandomIds(_IconCurtainCloseDark2);
const IconCurtainOpenDark1 = withRandomIds(_IconCurtainOpenDark1);
const IconCurtainOpenDark2 = withRandomIds(_IconCurtainOpenDark2);
const IconDevicesLampNoChange1 = withRandomIds(_IconDevicesLampNoChange1);
const IconDevicesLampNoChange2 = withRandomIds(_IconDevicesLampNoChange2);
const IconDevicesLampNoChange3 = withRandomIds(_IconDevicesLampNoChange3);
const IconDevicesLampNoChange4 = withRandomIds(_IconDevicesLampNoChange4);
const IconDevicesPlugNoChange = withRandomIds(_IconDevicesPlugNoChange);
const IconDevicesRelayNoChange1 = withRandomIds(_IconDevicesRelayNoChange1);
const IconDevicesRelayNoChange2 = withRandomIds(_IconDevicesRelayNoChange2);
const IconDevicesRelayNoChange3 = withRandomIds(_IconDevicesRelayNoChange3);
const IconDevicesRelayNoChange4 = withRandomIds(_IconDevicesRelayNoChange4);
const IconDevicesStatusNoChangeAr = withRandomIds(_IconDevicesStatusNoChangeAr);
const IconDevicesStatusNoChangeFa = withRandomIds(_IconDevicesStatusNoChangeFa);
const IconDevicesStatusNoChange = withRandomIds(_IconDevicesStatusNoChange);

export default function Devices({
    roomsData, roomsController, showDeleteAllDevicesButton,
    devices, negotiatedProtocol, currentUser, dataFetched }) {
    const s = useLocalizedStrings();
    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);
    const wm = useContext(WebViewManagerContext);

    const [selectedRoomId, setSelectedRoomId] = useState(undefined);
    useEffect(() => {
        if (selectedRoomId !== undefined) {
            const room = roomsData.rooms[selectedRoomId];
            if (room === undefined) {
                setSelectedRoomId(undefined);
            }
        }
    }, [roomsData.rooms, selectedRoomId]);

    const onCreateClick = useCallback(() => {
        hs.push("/devices/add", { roomId: selectedRoomId });
    }, [hs, selectedRoomId]);

    // const headerTopMargin = roomsData.roomsOrder.length > 0 ? (8 + 88 + 16) : 0;

    const filterMacs = useMemo(() => {
        if (selectedRoomId !== undefined) {
            const room = roomsData.rooms[selectedRoomId];
            if (room !== undefined) {
                return room["device_macs"];
            } else {
                return undefined;
            }
        }
        return undefined;
    }, [roomsData.rooms, selectedRoomId]);

    const [removeAllPopupId, showRemoveAllPopup, dismissRemoveAllPopup] = usePopup();

    const onDeleteAllClick = useCallback(() => {
        for (let i = 0; i < devices.length; i++) {
            const device = devices[i];
            if (filterMacs === undefined || filterMacs.includes(device["mac"])) {
                device.remove();
            }
        }
        dismissRemoveAllPopup();
    }, [devices, filterMacs, dismissRemoveAllPopup]);

    const headerLable = useMemo(() => {
        if (negotiatedProtocol.hasRoom) {
            if (selectedRoomId !== undefined) {
                const room = roomsData.rooms[selectedRoomId];
                if (room !== undefined) {
                    return room["name"];
                } else {
                    return s.myHome;
                }
            } else {
                return s.myHome;
            }
        } else {
            return s.devices
        }
    }, [roomsData.rooms, selectedRoomId, negotiatedProtocol, s]);

    const showRoomTabs = (negotiatedProtocol.hasRoom) && (roomsData.roomsOrder.length > 0);
    const showRemoveAllButton = dataFetched && negotiatedProtocol.hasDeviceSetting && currentUser.permissions.setting && showDeleteAllDevicesButton;
    const showAddButton = dataFetched && negotiatedProtocol.hasDeviceSetting && currentUser.permissions.setting && (
        !negotiatedProtocol.isSolo || (wm.webViewState.supportUDP && wm.webViewState.supportESPTouchV1)
    )

    return (
        <div className="device-list-page-container" style={{
            gridTemplateRows: showRoomTabs ? "auto auto 1fr" : "auto 1fr"
        }}>
            <RoomHeaderTab
                roomsData={roomsData} roomsController={roomsController}
                selectedRoomId={selectedRoomId} setSelectedRoomId={setSelectedRoomId} />

            <div className="device-list-header" style={{ marginTop: showRoomTabs ? 8 : undefined }}>
                <div className="label">{headerLable}</div>
                <div className="button-group">
                    {showRemoveAllButton ? (
                        <div className="button-with-border" onClick={showRemoveAllPopup}>
                            <div className="label" >
                                {s.deleteAllDevices}
                            </div>
                            <IconDeleteWhite className="icon" />
                        </div>
                    ) : null}
                    {showAddButton ? (
                        <div className="button-with-border" onClick={onCreateClick}>
                            <div className="label" >
                                {s.addDevice}
                            </div>
                            <IconPlus className="icon" />
                        </div>
                    ) : null}
                </div>
            </div>
            {dataFetched ?
                <DeviceList
                    devices={devices} showStates={true} showSubDevices={true}
                    filterMacs={filterMacs}
                    key={"home_page_device_list_of_room_" + String(selectedRoomId)}
                />
                : <div className={"device-list " + (p.inOverlayPage ? "" : "responsive")} >
                    <div className="empty-list-cell">{s.dataIsLoading}</div>
                </div>}

            {/* </div> */}

            <Popup popupId={removeAllPopupId} name="remove_all_devices_popup" baseZIndex={12}>
                <div className="remove-all-devices-popup">
                    <div className="header">{s.removeAllDevicesConfirmAreYouSure}</div>
                    <div className="buttons-group">
                        <div className="delete-button" onClick={onDeleteAllClick} style={{ gridColumnGap: 4 }}>
                            <div className="label">{s.removeAllDevicesConfirmOK}</div>
                            <IconDeleteRed className="icon" />
                        </div>
                        <div className="dismiss-button" onClick={dismissRemoveAllPopup}>{s.removeAllDevicesConfirmNO}</div>
                    </div>
                </div>
            </Popup>
        </div>
    );
}

const deviceGridSize = (device) => {
    switch (device.type) {
        case DeviceType.remote:
        case DeviceType.tuyaRemote:
            let subDevices = [];
            if (device.sub_devices) {
                subDevices = device.sub_devices.filter(
                    d => supportedRemoteDeviceTypes.includes(d.type));
            }
            if (subDevices.length <= 2) {
                return { c: 1, r: 1 };
            } else if (subDevices.length <= 4) {
                return { c: 2, r: 1 };
            } else {
                return { c: 2, r: Math.ceil(subDevices.length / 4) };
            }
        case DeviceType.dpst:
            return { c: 1, r: 1 };
        case DeviceType.amplifier:
        case DeviceType.evaporativeCooler:
        case DeviceType.relay4:
        case DeviceType.lampX4:
        case DeviceType.lampX3:
        case DeviceType.winep:
        case DeviceType.multipurposeSensor:
        case DeviceType.wc:
        case DeviceType.curtain1Lamp2:
            return { c: 2, r: 1 };
        case DeviceType.curtain:
            if (device.num_channels === undefined) {
                return { c: 1, r: 1 };
            }
            return { c: Math.max(1, Math.min(device.num_channels, 2)), r: 1 };
        case DeviceType.scenarioSwitch:
            return { c: 2, r: 1 };
        default:
            return { c: 1, r: 1 };
    }
};

const devicePriority = (device) => {
    // if (!device.active)
    //     return 0;

    switch (device.type) {
        case DeviceType.amplifier:
            return 17;
        case DeviceType.marsus:
        case DeviceType.sedna:
        case DeviceType.hinavaEcoThermostat:
            return 16;
        case DeviceType.evaporativeCooler:
            return 15;
        case DeviceType.remote:
        case DeviceType.tuyaRemote:
        case DeviceType.elevator1:
            return 14;
        case DeviceType.lamp:
        case DeviceType.lampX1TwoWay:
        case DeviceType.plug:
            return 13;
        case DeviceType.dpst:
            return 12;
        case DeviceType.lampX3:
            return 11;
        case DeviceType.lampX4:
        case DeviceType.winep:
            return 10;
        case DeviceType.relay4:
            return 9;
        case DeviceType.curtain1Lamp2:
            return 8;
        case DeviceType.curtain:
            return 7;
        case DeviceType.wc:
            return 6;
        case DeviceType.tvRemote:
        case DeviceType.splitRemote:
        case DeviceType.fanRemote:
        case DeviceType.homeTheatreRemote:
        case DeviceType.soundBarRemote:
        case DeviceType.receiverRemote:
        case DeviceType.projectorRemote:
            return 5;
        case DeviceType.multipurposeSensor:
            return 4;
        case DeviceType.doorbell:
        case DeviceType.siren:
        case DeviceType.motion:
        case DeviceType.doorSensor:
        case DeviceType.wifiDoorSensor:
        case DeviceType.wifiMotionSensor:
            return 3;
        case DeviceType.scenarioSwitch:
            return 2;
        default:
            return 1;
    }
};

export const supportedDeviceTypes = [
    DeviceType.plug,
    DeviceType.elevator1,
    // DeviceType.winep, // not released
    DeviceType.remote,
    DeviceType.tuyaRemote,
    DeviceType.lamp, DeviceType.lampX1TwoWay, DeviceType.dpst, DeviceType.lampX3, DeviceType.lampX4,
    DeviceType.relay4,
    DeviceType.curtain1Lamp2,
    DeviceType.motion, DeviceType.wifiMotionSensor,
    DeviceType.multipurposeSensor,
    DeviceType.wc,
    DeviceType.doorSensor, DeviceType.wifiDoorSensor,
    DeviceType.doorbell,
    DeviceType.siren,
    DeviceType.sedna, DeviceType.marsus, DeviceType.hinavaEcoThermostat,
    DeviceType.curtain,
    DeviceType.scenarioSwitch,
    DeviceType.amplifier,
    DeviceType.evaporativeCooler,
];

export const supportedRemoteDeviceTypes = [
    DeviceType.tvRemote,
    DeviceType.splitRemote,
    // DeviceType.receiverRemote,
    // DeviceType.fanRemote,
    // DeviceType.soundBarRemote,
    // DeviceType.projectorRemote,
    // DeviceType.homeTheatreRemote,
];

function refineDevices(devices, filterMacs) {
    let refinedDevices = [...devices];

    if (filterMacs !== undefined) {
        refinedDevices = refinedDevices.filter(d => filterMacs.includes(d["mac"]));
    }

    for (let i = 0; i < refinedDevices.length; i++) {
        const device = refinedDevices[i];

        if (device.type === DeviceType.curtain) {
            if (device.num_channels === undefined) {
                // It can create a bug with no-change state.
                // So, make sure `num_channels` is never undefined.
                if (device.channel2 !== undefined && device.channel2 !== null) {
                    device.num_channels = 2;
                } else if (device.channel1 !== undefined && device.channel1 !== null) {
                    device.num_channels = 1;
                }
            }
        }
    }

    return refinedDevices;
}

function reOrderDevices(devices) {
    let sortedDevice = [...devices];
    sortedDevice.sort((d1, d2) => {
        return devicePriority(d2) - devicePriority(d1)
    });

    sortedDevice = sortedDevice.filter(d => supportedDeviceTypes.includes(d.type));

    // // Comment later :))
    // let filterThisMacs = ["126fb745-0e34-4cac-a144-87ec65c6b5a9", "be74dc02-bea2-4847-b254-cee5214fb8aa", "53ace7f8-403d-48b5-998a-1d7383e4648f"];
    // sortedDevice = sortedDevice.filter(d => filterThisMacs.includes(d.mac));

    const reOrderedDevices = [];

    while (sortedDevice.length > 0) {
        const firstDevice = sortedDevice.shift();

        if (deviceGridSize(firstDevice).c === 1) {
            let foundPairDevice = false;
            for (let i = 0; i < sortedDevice.length; i++) {
                const device = sortedDevice[i];
                if (deviceGridSize(device).c === 1) {
                    sortedDevice.splice(i, 1);
                    reOrderedDevices.push(firstDevice);
                    reOrderedDevices.push(device);
                    foundPairDevice = true;
                    break;
                }
            }
            if (!foundPairDevice) {
                if (sortedDevice.length > 0) {
                    // another device with a bigger size exist
                    // & there is no other device to fill the gap, 
                    // so let that device to be on top of this device
                    sortedDevice.push(firstDevice);
                } else {
                    reOrderedDevices.push(firstDevice);
                }
            }
        } else {
            reOrderedDevices.push(firstDevice);
        }
    }

    return reOrderedDevices;
}

function calculateDeviceCellHeight(rowSize) {
    const cellHeightAdditional = 52 + 16;
    const cellHeightBase = 119 - cellHeightAdditional;

    return cellHeightBase + rowSize * cellHeightAdditional
}

const minAcceptableDeviceListWidth = 380 + 8;

// showStates=false will block device 'change state' functionality
export function DeviceList({
    devices, noDeviceText, filterMacs, canStateBeNoChange,
    selectionCallback, selectedDevices, showStates, showSubDevices, showIndicateButton,
    dontOpenSettings, showDCEvenWhenNotShowingStates,
    bottomPaddingSize,
    style, lang }) {
    const p = useContext(CurrentPageContext);
    const s = useLocalizedStrings();
    const { width: windowWidth } = useWindowDimensions();

    const widthForDeviceList = windowWidth - 16;
    let numberOfSections = Math.max(1, Math.floor(widthForDeviceList / minAcceptableDeviceListWidth)); // each section has two columns
    if (p.inOverlayPage) {
        numberOfSections = 1;
    }

    const _noDeviceText = noDeviceText ? noDeviceText : s.noDevice;
    const _bottomPaddingSize = bottomPaddingSize ? bottomPaddingSize : 16;

    const [hasAnyDevice, assembledDeviceInSectionsWithBlank] = useMemo(() => {
        const _selectedDevices = Array.isArray(selectedDevices) ? selectedDevices : [];
        let sectionElements = [];
        let sectionHeights = []
        for (let i = 0; i < numberOfSections; i++) {
            sectionElements.push([]);
            sectionHeights.push(0);
        }

        const refinedDevices = refineDevices(devices, filterMacs);
        const reOrderedDevices = reOrderDevices(refinedDevices);
        const _hasAnyDevice = reOrderedDevices.length > 0;

        let currentColumnPosition = 1;
        let currentSectionIndex = 0;
        let lastCellHeight = 0;
        for (let i = 0; i < reOrderedDevices.length; i++) {
            const device = reOrderedDevices[i];
            const gridSize = deviceGridSize(device);
            if (currentColumnPosition + gridSize.c > 1 + 2) { // let's go to a new row
                sectionHeights[currentSectionIndex] += lastCellHeight;
                currentColumnPosition = 1;

                let minHeight = Math.min(...sectionHeights);
                for (let j = 0; j < numberOfSections; j++) {
                    if (sectionHeights[j] <= minHeight) {
                        currentSectionIndex = j
                        break
                    }
                }
            }
            sectionElements[currentSectionIndex].push(
                <DeviceCell key={"device_mac_" + device.mac} device={device} canStateBeNoChange={canStateBeNoChange}
                    fromColumn={currentColumnPosition} toColumn={currentColumnPosition + gridSize.c}
                    rowSize={gridSize.r}
                    selectionCallback={selectionCallback} dontOpenSettings={dontOpenSettings}
                    showDCEvenWhenNotShowingStates={showDCEvenWhenNotShowingStates}
                    showStates={showStates} showIndicateButton={showIndicateButton}
                    isSelected={_selectedDevices.includes(device.mac)} selectedDevices={_selectedDevices} showSubDevices={showSubDevices} />
            )
            lastCellHeight = calculateDeviceCellHeight(gridSize.r)
            currentColumnPosition += gridSize.c; // size
        }

        for (let i = 0; i < numberOfSections; i++) {
            sectionElements[i].push(
                <div style={{ height: _bottomPaddingSize - 9, gridColumn: "1 / 3" }} key="device-list-padding" />
            );
        }

        return [_hasAnyDevice, sectionElements];
    }, [
        devices, selectionCallback, selectedDevices, _bottomPaddingSize,
        showStates, showSubDevices, showIndicateButton, filterMacs, dontOpenSettings,
        showDCEvenWhenNotShowingStates, canStateBeNoChange, numberOfSections
    ])


    if (hasAnyDevice) {
        let gridTemplateColumns = ""
        for (let i = 0; i < numberOfSections; i++) {
            gridTemplateColumns += "1fr";
            if (i !== (numberOfSections - 1)) {
                gridTemplateColumns += " ";
            }
        }
        return (
            <div className="device-list-sections" style={{ ...style, gridTemplateColumns }} lang={lang}>
                {
                    assembledDeviceInSectionsWithBlank.map(
                        (elements, sectionIndex) =>
                            <div className="device-list" key={"device-list-section" + String(sectionIndex)}>{elements}</div>)
                }
            </div>
        );
    } else {
        return (
            <div className="device-list-sections" style={{ ...style, gridTemplateColumns: '1fr' }} lang={lang}>
                <div className={"device-list " + (p.inOverlayPage ? "" : "responsive")}>
                    <div className="empty-list-cell">{_noDeviceText}</div>
                </div>
            </div>
        );
    }
}

function DeviceCell({
    device, isSelected, selectionCallback, showIndicateButton,
    fromColumn, toColumn, rowSize,
    showStates, canStateBeNoChange, dontOpenSettings, showSubDevices, selectedDevices,
    showDCEvenWhenNotShowingStates,
}) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const hs = useContext(HistoryStackContext);

    const onMoreClick = useCallback(() => {
        hs.push("/devices/detail", { mac: device.mac })
    }, [hs, device.mac]);

    const selectionOnClick = useCallback(() => {
        selectionCallback(device);
    }, [selectionCallback, device]);

    const [sirenState, setSirenState] = useState(null);
    const sirenBackPanel = useMemo(() => {
        switch (sirenState) {
            case 'alarming':
                return <div className="siren-status-backpanel alarming" />;
            case 'start-alarming':
                return <div className="siren-status-backpanel start-alarming" />;
            case 'not-alarming':
            default:
                return null;
        }
    }, [sirenState]);

    const darkName = showStates &&
        (
            (
                (device.type === DeviceType.doorbell) && (device.active) && (device.state)
            ) || (
                (device.type === DeviceType.elevator1) && (device.active) && (device.state1)
            ) || (
                (device.type === DeviceType.curtain) && (
                    (
                        (device.num_channels === 1) && (device.channel1 !== undefined) && (device.channel1 !== CurtainState.idle)
                    ) ||
                    (
                        (device.num_channels === 2) && (
                            (!l.isRTL && (device.channel1 !== undefined) && (device.channel1 !== CurtainState.idle)) ||
                            (l.isRTL && (device.channel2 !== undefined) && (device.channel2 !== CurtainState.idle))
                        )
                    )
                )
            ) || (
                (device.type === DeviceType.curtain1Lamp2) && (device.curtain !== undefined) && (device.curtain !== CurtainState.idle)
            ));
    const darkMoreButton = showStates &&
        (
            (
                (device.type === DeviceType.doorbell) && (device.active) && (device.state)
            ) || (
                (device.type === DeviceType.elevator1) && (device.active) && (device.state1)
            ) || (
                (device.type === DeviceType.curtain) && (
                    (
                        (device.num_channels === 1) && (device.channel1 !== CurtainState.idle)
                    ) ||
                    (
                        (device.num_channels === 2) && (
                            (!l.isRTL && (device.channel2 !== CurtainState.idle)) ||
                            (l.isRTL && (device.channel1 !== CurtainState.idle))
                        )
                    )
                )
            ));

    // sedna has no indicate functionality
    const _showIndicateButton = (showIndicateButton === true) && (device.type !== DeviceType.sedna);

    const indicateIconRef = useRef(null);
    const indicateIcon2Ref = useRef(null);
    const [indicateFlashing, setIndicateFlashing] = useState(false);
    const onIndicateClick = useCallback(() => {
        if (!indicateFlashing) {
            device.indicate();
            if (indicateIconRef.current)
                indicateIconRef.current.className = "device-indicate-icon-container flashing";
            if (indicateIcon2Ref.current)
                indicateIcon2Ref.current.className = "device-indicate-dot flashing";
            setTimeout(() => {
                try {
                    setIndicateFlashing(false);
                    if (indicateIconRef.current)
                        indicateIconRef.current.className = "device-indicate-icon-container";
                    if (indicateIcon2Ref.current)
                        indicateIcon2Ref.current.className = "device-indicate-dot";
                }
                catch (err) {
                    console.log(err);
                }
            }, 1500 * 7 + 100); // indicate-time
            setIndicateFlashing(true);
        }
    }, [device, indicateFlashing]);

    const showMuteIcon = (device.type === DeviceType.scenarioSwitch) && (!device.result_sound);

    const blurPanel = useMemo(() => {
        if (
            (showStates || showDCEvenWhenNotShowingStates) &&
            (device.active !== true) &&
            (device.type !== DeviceType.amplifier)
        ) {
            return <div className="blur-dc-panel white">
                <IconDisconnect className="dc-icon" />
                <div className="label">{s.deviceDC}</div>
            </div>
        }

        if (
            (device.addDeviceStatus !== undefined) &&
            (device.addDeviceStatus !== AddDeviceStatus.notSelected)
        ) {
            const ad = s.addDeviceProcess;
            switch (device.addDeviceStatus) {
                case AddDeviceStatus.pairing:
                    return <div className="blur-dc-panel white">
                        <IconThreeDotsLoadingWhite className="loading-icon" />
                        <div className="label">{ad.status.pairing}</div>
                    </div>;
                case AddDeviceStatus.joined:
                    return <div className="blur-dc-panel black">
                        <IconTickCircle className="tick-icon" />
                        <div className="label">{ad.status.joined}</div>
                    </div>;
                case AddDeviceStatus.connectionError:
                    return <div className="blur-dc-panel white">
                        <IconX className="x-icon" />
                        <div className="label">{ad.status.connectionError}</div>
                    </div>;
                case AddDeviceStatus.unknownError:
                default:
                    return <div className="blur-dc-panel white">
                        <IconX className="x-icon" />
                        <div className="label">{ad.status.unknownError}</div>
                    </div>;
            }
        }

        return null
    }, [device.active, device.addDeviceStatus, device.type,
    s.addDeviceProcess, s.deviceDC,
        showDCEvenWhenNotShowingStates, showStates]);

    return (
        <div className="device" dir="ltr"
            style={
                {
                    gridColumn: (fromColumn.toString() + " / " +
                        (toColumn).toString()),
                    height: calculateDeviceCellHeight(rowSize),
                }
            }>
            <div className="device-name"
                style={{ color: darkName ? "#151c21" : undefined }}>
                {device.name}
                {showMuteIcon ? <IconMute className="mute-icon" /> : null}
            </div>
            {device.roomName !== undefined ? (
                <div className="room-name"
                    style={{ color: darkName ? "#151c21" : undefined }}>
                    {device.roomName}
                </div>) : null}

            {
                (!showStates && _showIndicateButton) ?
                    <Fragment>
                        <div className="device-indicate-icon-container" ref={indicateIconRef}>
                            <IconIndicate className="icon" />
                        </div>
                        <div className="device-indicate-dot" ref={indicateIcon2Ref} />
                        <div className="device-indicate-overlay" onClick={onIndicateClick} />
                    </Fragment> : null
            }
            {
                (dontOpenSettings !== true) ?  // show more button
                    (darkMoreButton ?
                        <IconMoreDark className="device-more-action-image" /> :
                        <IconMore className="device-more-action-image" />)
                    : null
            }
            {showStates ? sirenBackPanel : null}
            {(dontOpenSettings !== true) ?  // show more button
                <div className="device-more-overlay" onClick={onMoreClick} />
                : null
            }

            <DeviceButtons
                device={device}
                canStateBeNoChange={canStateBeNoChange}
                showStates={showStates}
                notifySirenState={setSirenState}

                showSubDevices={showSubDevices}
                subDeviceSelectionCallback={selectionCallback} // may be undefined
                selectedDevices={selectedDevices}
                s={s} l={l} />

            {
                (
                    (selectionCallback !== undefined)
                    && (
                        (
                            (device.type !== DeviceType.tuyaRemote) && (device.type !== DeviceType.remote)
                        ) || !showSubDevices
                    )
                ) ? (
                    <div className={isSelected ? "selection-panel active" : "selection-panel"} onClick={selectionOnClick} />
                ) : null
            }
            {isSelected &&
                <IconSelection className="selection-tick" onClick={selectionOnClick} />
            }

            {blurPanel}
        </div>
    );
}

function DeviceButtons({
    device, showStates, canStateBeNoChange,
    showSubDevices, subDeviceSelectionCallback, selectedDevices,
    notifySirenState, s, l }) {
    const ds = s.deviceSettings;

    const onBoolClickH = useCallback((key) => {
        return (event) => {
            event.preventDefault();
            let changeStateObject = {}
            changeStateObject[key] = !device[key]
            device.changeState(changeStateObject);
        }
    }, [device]);
    const onCoolerPowerClick = useCallback((event) => {
        event.preventDefault();
        let changeStateObject = {}
        if (device["power"] === EvaporativeCoolerPower.off) {
            changeStateObject["power"] = EvaporativeCoolerPower.on;
        } else {
            changeStateObject["power"] = EvaporativeCoolerPower.off;
        }
        device.changeState(changeStateObject);
    }, [device]);
    const onClickHV = useCallback((key, value) => {
        return (event) => {
            event.preventDefault();
            let changeStateObject = {}
            changeStateObject[key] = value
            device.changeState(changeStateObject);
        }
    }, [device]);

    switch (device.type) {
        case DeviceType.amplifier:
            return (
                <AudioControllerContents device={device} showStates={showStates} />
            );
        case DeviceType.plug:
            return (
                <div className="device-buttons">
                    <PlugButton on={device.state} onClickH={onBoolClickH("state")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        waitingForChangeState={device.waitingForChangeState["state"]} />
                </div>
            );
        case DeviceType.winep:
            return (
                <div className="device-buttons winep-buttons">
                    <div className="leds">
                        <div className="led" />
                        <div className="led" />
                        <div className="led" />
                        <div className="led" />
                    </div>
                    <PlugButton on={device.state1} onClickH={onBoolClickH("state1")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        waitingForChangeState={device.waitingForChangeState["state1"]} />
                    <PlugButton on={device.state2} onClickH={onBoolClickH("state2")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        waitingForChangeState={device.waitingForChangeState["state2"]} />
                    <PlugButton on={device.state3} onClickH={onBoolClickH("state3")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        waitingForChangeState={device.waitingForChangeState["state3"]} />
                    <PlugButton on={device.state4} onClickH={onBoolClickH("state4")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        waitingForChangeState={device.waitingForChangeState["state4"]} />
                </div>
            );
        case DeviceType.curtain:
            if (device.num_channels === 1) {
                return (
                    <Fragment>
                        <div className="device-buttons">
                            <CurtainSingleButton channel={1} open={true} buttonOrder={1}
                                onClickH={onClickHV("channel1", CurtainState.open)}
                                showStates={showStates} />
                            <CurtainSingleButton channel={1} open={false} buttonOrder={2}
                                onClickH={onClickHV("channel1", CurtainState.close)}
                                showStates={showStates} />
                        </div>
                        {
                            !showStates ? null :
                                <CurtainStatePanel
                                    side="full" channel={1}
                                    state={device.channel1}
                                    onClickH={onClickHV("channel1", CurtainState.idle)}
                                    waitingForChangeState={device.waitingForChangeState["channel1"]} />
                        }
                    </Fragment>
                )
            } else if (device.num_channels === 2) {
                return (
                    <Fragment>
                        <div className="device-buttons">
                            <CurtainSingleButton channel={1} open={true} buttonOrder={1}
                                onClickH={onClickHV("channel1", CurtainState.open)}
                                showStates={showStates} />
                            <CurtainSingleButton channel={1} open={false} buttonOrder={2}
                                onClickH={onClickHV("channel1", CurtainState.close)}
                                showStates={showStates} />
                            <CurtainSingleButton channel={2} open={true} buttonOrder={1}
                                onClickH={onClickHV("channel2", CurtainState.open)}
                                showStates={showStates} />
                            <CurtainSingleButton channel={2} open={false} buttonOrder={2}
                                onClickH={onClickHV("channel2", CurtainState.close)}
                                showStates={showStates} />
                        </div>
                        {
                            !showStates ? null :
                                <CurtainStatePanel
                                    side="half-left" channel={1}
                                    state={device.channel1}
                                    onClickH={onClickHV("channel1", CurtainState.idle)}
                                    waitingForChangeState={device.waitingForChangeState["channel1"]} />
                        }
                        {
                            !showStates ? null :
                                <CurtainStatePanel
                                    side="half-right" channel={2}
                                    state={device.channel2}
                                    onClickH={onClickHV("channel2", CurtainState.idle)}
                                    waitingForChangeState={device.waitingForChangeState["channel2"]} />
                        }
                    </Fragment>
                )
            } else if (device.num_channels === undefined) { // in add-device
                return <div className="device-buttons">
                    <SymbolicCurtainButton />
                </div>;
            } else {
                return null;
            }
        case DeviceType.lamp:
        case DeviceType.lampX1TwoWay:
            return (
                <div className="device-buttons">
                    <LampButton on={device.state} buttonOrder={1} onClickH={onBoolClickH("state")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.switch1_name !== undefined ?
                                device.switch1_name : ds.switchName1Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state"]}
                    />
                </div>
            )
        case DeviceType.dpst:
            return (
                <div className="device-buttons">
                    <LampButton on={device.state1} buttonOrder={1} onClickH={onBoolClickH("state1")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.switch1_name !== undefined ?
                                device.switch1_name : ds.switchName1Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state1"]} />
                    <LampButton on={device.state2} buttonOrder={2} onClickH={onBoolClickH("state2")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.switch2_name !== undefined ?
                                device.switch2_name : ds.switchName2Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state2"]} />
                </div>
            )
        case DeviceType.lampX3:
            return (
                <div className="device-buttons">
                    <LampButton on={device.state1} buttonOrder={1} onClickH={onBoolClickH("state1")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.switch1_name !== undefined ?
                                device.switch1_name : ds.switchName1Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state1"]} />
                    <LampButton on={device.state2} buttonOrder={2} onClickH={onBoolClickH("state2")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.switch2_name !== undefined ?
                                device.switch2_name : ds.switchName2Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state2"]} />
                    <LampButton on={device.state3} buttonOrder={3} onClickH={onBoolClickH("state3")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.switch3_name !== undefined ?
                                device.switch3_name : ds.switchName3Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state3"]} />
                </div>
            )
        case DeviceType.lampX4:
            return (
                <div className="device-buttons">
                    <LampButton on={device.state1} buttonOrder={1} onClickH={onBoolClickH("state1")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.switch1_name !== undefined ?
                                device.switch1_name : ds.switchName1Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state1"]} />
                    <LampButton on={device.state2} buttonOrder={2} onClickH={onBoolClickH("state2")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.switch2_name !== undefined ?
                                device.switch2_name : ds.switchName2Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state2"]} />
                    <LampButton on={device.state3} buttonOrder={3} onClickH={onBoolClickH("state3")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.switch3_name !== undefined ?
                                device.switch3_name : ds.switchName3Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state3"]} />
                    <LampButton on={device.state4} buttonOrder={4} onClickH={onBoolClickH("state4")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.switch4_name !== undefined ?
                                device.switch4_name : ds.switchName4Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state4"]} />
                </div>
            )
        case DeviceType.curtain1Lamp2:
            return (
                <Fragment>
                    <div className="device-buttons">
                        <LampButton on={device.lamp1} buttonOrder={1} onClickH={onBoolClickH("lamp1")}
                            showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                            switchName={
                                device.switch1_name !== undefined ?
                                    device.switch1_name : ds.switchName1Default}
                            useCustomSwitchNames={device.use_custom_switch_names}
                            waitingForChangeState={device.waitingForChangeState["lamp1"]} />
                        <LampButton on={device.lamp2} buttonOrder={2} onClickH={onBoolClickH("lamp2")}
                            showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                            switchName={
                                device.switch2_name !== undefined ?
                                    device.switch2_name : ds.switchName2Default}
                            useCustomSwitchNames={device.use_custom_switch_names}
                            waitingForChangeState={device.waitingForChangeState["lamp2"]} />
                        <CurtainSingleButton channel={1} open={true} buttonOrder={1}
                            onClickH={onClickHV("curtain", CurtainState.open)}
                            showStates={showStates} />
                        <CurtainSingleButton channel={1} open={false} buttonOrder={2}
                            onClickH={onClickHV("curtain", CurtainState.close)}
                            showStates={showStates} />
                    </div>
                    {
                        !showStates ? null :
                            <CurtainStatePanel
                                side="half-right" channel={1}
                                state={device.curtain}
                                onClickH={onClickHV("curtain", CurtainState.idle)}
                                waitingForChangeState={device.waitingForChangeState["curtain"]} />
                    }
                </Fragment>
            )
        case DeviceType.wc:
            return <Fragment>
                <WCScenarioTag s={s} l={l} device={device} />
                <div className="device-buttons">
                    <WCButton on={device.lamp} buttonOrder={1} onClickH={onBoolClickH("lamp")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        waitingForChangeState={device.waitingForChangeState["lamp"]} />
                    <WCButton on={device.fan} buttonOrder={2} onClickH={onBoolClickH("fan")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        waitingForChangeState={device.waitingForChangeState["fan"]} />
                    <WCButton on={device.spray} buttonOrder={3} onClickH={onBoolClickH("spray")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        waitingForChangeState={device.waitingForChangeState["spray"]} />
                    <WCButton on={device.music} buttonOrder={4} onClickH={onBoolClickH("music")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        waitingForChangeState={device.waitingForChangeState["music"]} />
                </div>
            </Fragment>
        case DeviceType.evaporativeCooler:
            return (
                <div className="device-buttons">
                    <CoolerButton state={device.power} buttonOrder={1} onClickH={onCoolerPowerClick}
                        showStates={showStates}
                        useCustomSwitchNames={false}
                        waitingForChangeState={device.waitingForChangeState["power"]} />
                    <CoolerButton state={device.pump} buttonOrder={2} onClickH={onBoolClickH("pump")}
                        showStates={showStates}
                        useCustomSwitchNames={false}
                        waitingForChangeState={device.waitingForChangeState["pump"]} />
                    <CoolerButton state={device.slow_fan} buttonOrder={3} onClickH={onBoolClickH("slow_fan")}
                        showStates={showStates}
                        useCustomSwitchNames={false}
                        waitingForChangeState={device.waitingForChangeState["slow_fan"]} />
                    <CoolerButton state={device.fast_fan} buttonOrder={4} onClickH={onBoolClickH("fast_fan")}
                        showStates={showStates}
                        useCustomSwitchNames={false}
                        waitingForChangeState={device.waitingForChangeState["fast_fan"]} />
                </div>
            )
        case DeviceType.relay4:
            return (
                <div className="device-buttons">
                    <RelayButton on={device.channel1} buttonOrder={1} onClickH={onBoolClickH("channel1")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.channel1_name !== undefined ?
                                device.channel1_name : ds.channelName1Default}
                        useCustomSwitchNames={device.use_custom_channel_names}
                        waitingForChangeState={device.waitingForChangeState["channel1"]} />
                    <RelayButton on={device.channel2} buttonOrder={2} onClickH={onBoolClickH("channel2")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.channel2_name !== undefined ?
                                device.channel2_name : ds.channelName2Default}
                        useCustomSwitchNames={device.use_custom_channel_names}
                        waitingForChangeState={device.waitingForChangeState["channel2"]} />
                    <RelayButton on={device.channel3} buttonOrder={3} onClickH={onBoolClickH("channel3")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.channel3_name !== undefined ?
                                device.channel3_name : ds.channelName3Default}
                        useCustomSwitchNames={device.use_custom_channel_names}
                        waitingForChangeState={device.waitingForChangeState["channel3"]} />
                    <RelayButton on={device.channel4} buttonOrder={4} onClickH={onBoolClickH("channel4")}
                        showStates={showStates} canStateBeNoChange={canStateBeNoChange}
                        switchName={
                            device.channel4_name !== undefined ?
                                device.channel4_name : ds.channelName4Default}
                        useCustomSwitchNames={device.use_custom_channel_names}
                        waitingForChangeState={device.waitingForChangeState["channel4"]} />
                </div>
            )
        case DeviceType.scenarioSwitch:
            return (
                <div className="device-buttons">
                    <ScenarioSwitchButton shape="o" colorCode={device.color1} scenarioId={device.scenario1} />
                    <ScenarioSwitchButton shape="x" colorCode={device.color2} scenarioId={device.scenario2} />
                    <ScenarioSwitchButton shape="t" colorCode={device.color3} scenarioId={device.scenario3} />
                    <ScenarioSwitchButton shape="r" colorCode={device.color4} scenarioId={device.scenario4} />
                </div>
            )
        case DeviceType.remote:
        case DeviceType.tuyaRemote:
            return <RemoteButtons
                device={device}
                showSubDevices={showSubDevices}
                subDeviceSelectionCallback={subDeviceSelectionCallback}
                selectedDevices={selectedDevices} />;
        case DeviceType.siren:
            return (
                <div className="device-buttons">
                    <SirenButton on={device.state && device.active} changeState={device.changeState}
                        showStates={showStates} notifySirenState={notifySirenState}
                        waitingForChangeState={device.waitingForChangeState["state"]} />
                </div>
            );
        case DeviceType.doorbell:
            return (
                <Fragment>
                    <div className="device-buttons">
                        <DoorbellSingleButton
                            onClickH={onClickHV("state", true)}
                            showStates={showStates} s={s} />
                    </div>
                    {
                        ((!showStates) || (!device.active) || (!device.state)) ? null :
                            <DoorbellStatePanel
                                s={s} forDeviceList={true}
                                onClickH={() => { device.changeState({ state: false }) }}
                                waitingForChangeState={device.waitingForChangeState["state"]} />
                    }
                </Fragment>
            )
        case DeviceType.elevator1:
            return (
                <Fragment>
                    <div className="device-buttons">
                        <ElevatorSingleButton
                            onClickH={onClickHV("state1", true)}
                            showStates={showStates} s={s} />
                    </div>
                    {
                        ((!showStates) || (!device.active) || (!device.state1)) ? null :
                            <ElevatorStatePanel
                                s={s} forDeviceList={true}
                                onClickH={() => { device.changeState({ state1: false }) }}
                                waitingForChangeState={device.waitingForChangeState["state1"]} />
                    }
                </Fragment>
            )
        case DeviceType.motion:
        case DeviceType.wifiMotionSensor:
            return (
                <div className="device-buttons">
                    <MotionSensorButton on={device.state} showStates={showStates} />
                </div>
            );
        case DeviceType.doorSensor:
        case DeviceType.wifiDoorSensor:
            return (
                <div className="device-buttons">
                    <DoorSensorButton on={device.state} showStates={showStates} />
                </div>
            );
        case DeviceType.multipurposeSensor:
            return (
                <div className="device-buttons">
                    <MultipurposeSensorButtons device={device} showStates={showStates} />
                </div>
            );
        case DeviceType.sedna:
            if ((device.sub_type !== SednaInfo.subType.volkan) && showStates)
                return <div />;
        // eslint-disable-next-line no-fallthrough
        case DeviceType.marsus:
            return (
                <div className="device-buttons">
                    <ThermostatButton device={device} showStates={showStates}
                        waitingForChangeOn={device.waitingForChangeState["is_on"]}
                        waitingForChangeMode={device.waitingForChangeState["mode"]} />
                </div>
            );
        case DeviceType.hinavaEcoThermostat:
            return (
                <div className="device-buttons">
                    <ThermostatButton device={device} showStates={showStates}
                        waitingForChangeOn={device.waitingForChangeState["is_on"]}
                        waitingForChangeMode={device.waitingForChangeState["mode_setting"]} />
                </div>
            );
        default:
            return <div />;
    }
}

function MultipurposeSensorButtons({ device, showStates }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    if (!showStates) {
        return (
            <Fragment>
                <div className="device-button">
                    <IconTemperature3 className="device-button-icon-on" />
                    <div className="status-placeholder" />
                </div>
                <div className="device-button">
                    <IconMotionDetected className="device-button-icon-on" />
                    <div className="status-placeholder" />
                </div>
                <div className="device-button">
                    <IconSoundDetected className="device-button-icon-on" />
                    <div className="status-placeholder" />
                </div>
                <div className="device-button">
                    <IconLightNormal className="device-button-icon-on" />
                    <div className="status-placeholder" />
                </div>
            </Fragment>
        );
    } else {
        return (
            <Fragment>
                <div className="device-button" style={{ width: 70 }}>
                    {device.temperature <= 20 ?
                        <IconTemperature1 className="device-button-icon-on" /> :
                        (device.temperature <= 25 ?
                            <IconTemperature2 className="device-button-icon-on" /> :
                            (device.temperature <= 30 ?
                                <IconTemperature3 className="device-button-icon-on" /> :
                                <IconTemperature4 className="device-button-icon-on" />))}
                    <div dir="rtl" className="device-on-label status-text-or-icon text-label-width-fixer">
                        {s.cDegreeFunc(l.localizeNumber(device.temperature))}
                    </div>
                </div>
                <div className="device-button" style={{ width: 70 }}>
                    {device.motion ?
                        <IconMotionDetected className="device-button-icon-on" /> :
                        <IconMotionStandby className="device-button-icon-on" />}
                    {device.motion ? (
                        l.language === 'en' ?
                            <IconStatusMotionDetected className="status-text-or-icon" /> :
                            l.language === 'fa' ?
                                <IconStatusMotionDetectedFa className="status-text-or-icon" /> :
                                <IconStatusMotionDetectedAr className="status-text-or-icon" />
                    ) :
                        <div className="device-off-label">{s.motionNotDetected}</div>}
                </div>
                <div className="device-button" style={{ width: 70 }}>
                    {device.sound ?
                        <IconSoundDetected className="device-button-icon-on" /> :
                        <IconSoundMute className="device-button-icon-on" />}
                    {device.sound ? (
                        l.language === 'en' ?
                            <IconStatusSoundDetected className="status-text-or-icon" /> :
                            l.language === 'fa' ?
                                <IconStatusSoundDetectedFa className="status-text-or-icon" /> :
                                <IconStatusSoundDetectedAr className="status-text-or-icon" />
                    ) :
                        <div className="device-off-label">{s.soundNotDetected}</div>}
                </div>
                <div className="device-button" style={{ width: 70 }}>
                    {device.light_level <= 1 ?
                        // 0, 1
                        <IconLightOff className="device-button-icon-on" /> :
                        (device.light_level <= 4 ?
                            // 2, 3, 4
                            <IconLightNormal className="device-button-icon-on" /> :
                            // 5, 6
                            <IconLightBright className="device-button-icon-on" />)}
                    <div className="device-on-label status-text-or-icon text-label-width-fixer">{s.lightLevels[device.light_level]}</div>
                </div>
            </Fragment>

        );
    }
}

function DoorSensorButton({ on, showStates }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    let deviceIcon = null;
    let label = null;

    if (on) {
        if (l.language === 'en') {
            label = <IconStatusOpen className="status-text-or-icon" />;
        } else if (l.language === 'fa') {
            label = <IconStatusOpenFa className="status-text-or-icon" />;
        } else if (l.language === 'ar') {
            label = <IconStatusOpenAr className="status-text-or-icon" />;
        }
        deviceIcon = IconDoorOpen;
    } else {
        label = (
            <div className="device-off-label">{s.closed}</div>
        )
        deviceIcon = IconDoorClose;
    }

    if (!showStates) {
        return (
            <div className="device-button">
                <IconDoorOpen />
                <div className="status-placeholder" />
            </div>
        );
    } else {
        return (
            <div className="device-button">
                {React.createElement(deviceIcon, {
                    className: "device-button-icon-on" // (on ? "device-button-icon-on" : "device-button-icon-off")
                })}
                {label}
            </div>
        );
    }
}

function MotionSensorButton({ on, showStates }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    let deviceIcon = null;
    let label = null;

    if (on) {
        if (l.language === 'en') {
            label = <IconStatusDetected className="status-text-or-icon" />;
        } else if (l.language === 'fa') {
            label = <IconStatusDetectedFa className="status-text-or-icon" />;
        } else if (l.language === 'ar') {
            label = <IconStatusDetectedAr className="status-text-or-icon" />;
        }
        deviceIcon = IconMotionDetected;
    } else {
        label = (
            <div className="device-off-label">{s.motionNotDetected}</div>
        )
        deviceIcon = IconMotionStandby;
    }

    if (!showStates) {
        return (
            <div className="device-button">
                <IconMotionDetected />
                <div className="status-placeholder" />
            </div>
        );
    } else {
        return (
            <div className="device-button">
                {React.createElement(deviceIcon, {
                    className: "device-button-icon-on" // (on ? "device-button-icon-on" : "device-button-icon-off")
                })}
                {label}
            </div>
        );
    }
}

export function SirenButton({ on, changeState, showStates, notifySirenState, waitingForChangeState }) {
    // for this device: on <== device.state && device.active
    const l = useContext(LocalizationContext);
    const ss = l.strings.sirenState;

    // not-alarming | start-alarming | alarming
    const [sirenState, setSirenState] = useState('not-alarming');
    const [sirenAlarmTimeout, setSirenAlarmTimeout] = useState(null);

    useEffect(() => {
        if (sirenAlarmTimeout) {
            if (on) { // still on touch
                setSirenState('alarming');
            } else {
                setSirenState('start-alarming');
            }
        } else {
            if (on) {
                setSirenState('alarming');
            } else {
                setSirenState('not-alarming');
            }
        }
    }, [on, sirenAlarmTimeout]);

    useEffect(() => {
        if (notifySirenState)
            notifySirenState(sirenState);
    }, [sirenState, notifySirenState]);


    const onDown = useCallback((e) => {
        e.preventDefault();
        if (sirenAlarmTimeout) {
            if (on) {
                // nothing
            } else {
                // nothing
            }
        } else {
            if (on) {
                changeState({ state: false });
            } else {
                setSirenAlarmTimeout(setTimeout(() => {
                    changeState({ state: true });
                }, 3000)); // search for sirenAlarmTimeout in Devices.scss
            }
        }
    }, [changeState, on, sirenAlarmTimeout]);

    const onUp = useCallback((e) => {
        e.preventDefault();
        if (sirenAlarmTimeout) {
            if (on) {
                if (sirenAlarmTimeout) {
                    clearTimeout(sirenAlarmTimeout);
                    setSirenAlarmTimeout(null);
                }
            } else {
                if (sirenAlarmTimeout) {
                    clearTimeout(sirenAlarmTimeout);
                    setSirenAlarmTimeout(null);
                }
            }
        } else {
            if (on) {
                // nothing (changed on down)
            } else {
                // nothing
            }
        }
    }, [sirenAlarmTimeout, on]);

    const [deviceIcon, labelText, labelClass] = useMemo(() => {
        let deviceIcon = undefined;
        let labelText = undefined;
        let labelClass = undefined;
        switch (sirenState) {
            case 'alarming':
                deviceIcon = IconSirenOnWhite;
                labelText = ss.alarming;
                labelClass = 'device-siren-on-label status-text-or-icon';
                break;
            case 'start-alarming':
                deviceIcon = IconSirenOnWhite;
                labelText = ss.startAlarming;
                labelClass = 'device-siren-on-label status-text-or-icon';
                break;
            case 'not-alarming':
            default:
                deviceIcon = IconSirenOff;
                labelText = ss.notAlarming;
                labelClass = 'device-off-label status-text-or-icon';
                break;
        }

        return [deviceIcon, labelText, labelClass];
    }, [sirenState, ss]);

    const shouldShowWaiting = waitingForChangeState && !waitingForChangeState.ignoreWaiting;
    const label = useMemo(() => {
        if (shouldShowWaiting) {
            switch (sirenState) {
                case 'alarming':
                    return <IconThreeDotsLoadingWhite className="dots-icon" />;
                case 'start-alarming':
                    return <IconThreeDotsLoadingWhite className="dots-icon" />;
                case 'not-alarming':
                default:
                    return <IconThreeDotsLoadingGray className="dots-icon" />;
            }
        } else {
            return <div className={labelClass}>{labelText}</div>;
        }
    }, [sirenState, labelClass, labelText, shouldShowWaiting]);



    if (!showStates) {
        return (
            <div className="device-button">
                <IconSirenOn />
                <div className="status-placeholder" />
            </div>
        );
    } else {
        return (
            <div className="device-button">
                {React.createElement(deviceIcon, {
                    className: "device-button-icon-on"
                })}
                {label}
                <div className="device-on-click-overlay"
                    onMouseDown={onDown} onMouseUp={onUp} onMouseOut={onUp}
                    onTouchStart={onDown} onTouchEnd={onUp} />
            </div>
        );
    }
}

export function PlugButton({ on, onClickH, showStates, waitingForChangeState, canStateBeNoChange }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    const shouldShowWaiting = waitingForChangeState && !waitingForChangeState.ignoreWaiting;

    const [deviceIcon, label] = useMemo(() => {
        let deviceIcon = null;
        let label = null;

        if (canStateBeNoChange && (on === undefined)) {
            if (l.language === 'en') {
                label = <IconDevicesStatusNoChange className="status-text-or-icon" />;
            } else if (l.language === 'fa') {
                label = <IconDevicesStatusNoChangeFa className="status-text-or-icon" />;
            } else if (l.language === 'ar') {
                label = <IconDevicesStatusNoChangeAr className="status-text-or-icon" />;
            }
            deviceIcon = IconDevicesPlugNoChange;
        } else if (on) {
            if (l.language === 'en') {
                label = <IconStatusON className="status-text-or-icon" />;
            } else if (l.language === 'fa') {
                label = <IconStatusONFa className="status-text-or-icon" />;
            } else if (l.language === 'ar') {
                label = <IconStatusONAr className="status-text-or-icon" />;
            }
            deviceIcon = IconPlugOn;
        } else {
            label = (
                <div className="device-off-label status-text-or-icon">{s.off}</div>
            )
            deviceIcon = IconPlugOff;
        }

        if (shouldShowWaiting) {
            if (on) {
                label = <IconThreeDotsLoadingGradient className="dots-icon" />;
            } else {
                label = <IconThreeDotsLoadingGray className="dots-icon" />;
            }
        }

        return [deviceIcon, label]
    }, [l.language, s.off, on, shouldShowWaiting, canStateBeNoChange]);

    // console.log("PlugButton", on, deviceIcon, label);

    if (!showStates) {
        return (
            <div className="device-button">
                <IconPlugOn />
                <div className="status-placeholder" />
            </div>
        );
    } else {
        return (
            <div className="device-button"
                style={{ width: 54 }}> {/* set width > خاموش, for better placement of buttons and not changing on state changes*/}
                {React.createElement(deviceIcon, {
                    className: (on === false ? "device-button-icon-off" : "device-button-icon-on")
                })}
                {label}
                <div className="device-on-click-overlay" onClick={onClickH} />
            </div>
        );
    }
}

export function LampButton({
    on, buttonOrder, onClickH, showStates, waitingForChangeState,
    switchName, useCustomSwitchNames, canStateBeNoChange }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    let deviceIcon = IconLampOff1;
    let label = null;

    if (canStateBeNoChange && (on === undefined)) {
        switch (buttonOrder) {
            case 1:
                deviceIcon = IconDevicesLampNoChange1;
                break;
            case 2:
                deviceIcon = IconDevicesLampNoChange2;
                break;
            case 3:
                deviceIcon = IconDevicesLampNoChange3;
                break;
            case 4:
                deviceIcon = IconDevicesLampNoChange4;
                break;
            default:
                break;
        }
        if (l.language === 'en') {
            label = <IconDevicesStatusNoChange className="status-text-or-icon" />;
        } else if (l.language === 'fa') {
            label = <IconDevicesStatusNoChangeFa className="status-text-or-icon" />;
        } else if (l.language === 'ar') {
            label = <IconDevicesStatusNoChangeAr className="status-text-or-icon" />;
        }
    } else if (on || !showStates) {
        if (useCustomSwitchNames) {
            label = <div className="device-on-label status-text-or-icon text-label-width-fixer">{switchName}</div>;
        } else {
            if (l.language === 'en') {
                label = <IconStatusON className="status-text-or-icon" />;
            } else if (l.language === 'fa') {
                label = <IconStatusONFa className="status-text-or-icon" />;
            } else if (l.language === 'ar') {
                label = <IconStatusONAr className="status-text-or-icon" />;
            }
        }
        switch (buttonOrder) {
            case 1:
                deviceIcon = IconLampOn1;
                break;
            case 2:
                deviceIcon = IconLampOn2;
                break;
            case 3:
                deviceIcon = IconLampOn3;
                break;
            case 4:
                deviceIcon = IconLampOn4;
                break;
            default:
                break;
        }
    } else {
        if (useCustomSwitchNames) {
            label = <div className="device-off-label status-text-or-icon text-label-width-fixer">{switchName}</div>;
        } else {
            label = (
                <div className="device-off-label status-text-or-icon">{s.off}</div>
            )
        }
        switch (buttonOrder) {
            case 1:
                deviceIcon = IconLampOff1;
                break;
            case 2:
                deviceIcon = IconLampOff2;
                break;
            case 3:
                deviceIcon = IconLampOff3;
                break;
            case 4:
                deviceIcon = IconLampOff4;
                break;
            default:
                break;
        }
    }

    const shouldShowWaiting = waitingForChangeState && !waitingForChangeState.ignoreWaiting;
    if (shouldShowWaiting) {
        if (on) {
            label = <IconThreeDotsLoadingGradient className="dots-icon" />;
        } else {
            label = <IconThreeDotsLoadingGray className="dots-icon" />;
        }
    }

    if (!showStates) {
        return (
            <div className="device-button">
                {React.createElement(deviceIcon, { className: "device-button-icon-on" })}
                <div className="status-placeholder" />
            </div>
        );
    } else {
        return (
            <div className="device-button"
                style={{ width: useCustomSwitchNames ? 70 : 54 }}>
                {/* set width > خاموش, for better placement of buttons and not changing on state changes*/}
                {/* set width to 70, for better display of texts */}
                {React.createElement(deviceIcon, {
                    className: (on === false ? "device-button-icon-off" : "device-button-icon-on")
                })}
                {label}
                <div className="device-on-click-overlay" onClick={onClickH} />
            </div>
        );
    }
}

export function RelayButton({
    on, buttonOrder, onClickH, showStates, waitingForChangeState,
    switchName, useCustomSwitchNames, canStateBeNoChange }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    let deviceIcon = IconRelayOff1;
    let label = null;

    if (canStateBeNoChange && (on === undefined)) {
        switch (buttonOrder) {
            case 1:
                deviceIcon = IconDevicesRelayNoChange1;
                break;
            case 2:
                deviceIcon = IconDevicesRelayNoChange2;
                break;
            case 3:
                deviceIcon = IconDevicesRelayNoChange3;
                break;
            case 4:
                deviceIcon = IconDevicesRelayNoChange4;
                break;
            default:
                break;
        }
        if (l.language === 'en') {
            label = <IconDevicesStatusNoChange className="status-text-or-icon" />;
        } else if (l.language === 'fa') {
            label = <IconDevicesStatusNoChangeFa className="status-text-or-icon" />;
        } else if (l.language === 'ar') {
            label = <IconDevicesStatusNoChangeAr className="status-text-or-icon" />;
        }
    } else if (on || !showStates) {
        if (useCustomSwitchNames) {
            label = <div className="device-on-label status-text-or-icon text-label-width-fixer">{switchName}</div>;
        } else {
            if (l.language === 'en') {
                label = <IconStatusON className="status-text-or-icon" />;
            } else if (l.language === 'fa') {
                label = <IconStatusONFa className="status-text-or-icon" />;
            } else if (l.language === 'ar') {
                label = <IconStatusONAr className="status-text-or-icon" />;
            }
        }
        switch (buttonOrder) {
            case 1:
                deviceIcon = IconRelayOn1;
                break;
            case 2:
                deviceIcon = IconRelayOn2;
                break;
            case 3:
                deviceIcon = IconRelayOn3;
                break;
            case 4:
                deviceIcon = IconRelayOn4;
                break;
            default:
                break;
        }
    } else {
        if (useCustomSwitchNames) {
            label = <div className="device-off-label status-text-or-icon text-label-width-fixer">{switchName}</div>;
        } else {
            label = (
                <div className="device-off-label status-text-or-icon">{s.off}</div>
            )
        }
        switch (buttonOrder) {
            case 1:
                deviceIcon = IconRelayOff1;
                break;
            case 2:
                deviceIcon = IconRelayOff2;
                break;
            case 3:
                deviceIcon = IconRelayOff3;
                break;
            case 4:
                deviceIcon = IconRelayOff4;
                break;
            default:
                break;
        }
    }

    const shouldShowWaiting = waitingForChangeState && !waitingForChangeState.ignoreWaiting;
    if (shouldShowWaiting) {
        if (on) {
            label = <IconThreeDotsLoadingGradient className="dots-icon" />;
        } else {
            label = <IconThreeDotsLoadingGray className="dots-icon" />;
        }
    }

    if (!showStates) {
        return (
            <div className="device-button">
                {React.createElement(deviceIcon, { className: "device-button-icon-on" })}
                <div className="status-placeholder" />
            </div>
        );
    } else {
        return (
            <div className="device-button"
                style={{ width: useCustomSwitchNames ? 70 : 54 }}>
                {/* set width > خاموش, for better placement of buttons and not changing on state changes*/}
                {/* set width to 70, for better display of texts */}
                {React.createElement(deviceIcon, {
                    className: (on === false ? "device-button-icon-off" : "device-button-icon-on")
                })}
                {label}
                <div className="device-on-click-overlay" onClick={onClickH} />
            </div>
        );
    }
}

export function ScenarioSwitchButton({ shape, scenarioId, colorCode }) {
    const gw = useContext(GatewayDataContext);

    const scenarios = gw.gateway.scenarios;
    const scenario = useMemo(() => {
        return scenarios.find(({ id }) => id === scenarioId);
    }, [scenarios, scenarioId]);
    const isValid = (scenarioId !== -1) && (scenarioId !== undefined) && (scenario !== undefined);

    let label = "一";
    let color = "#595757";
    if (isValid) {
        label = scenario.name;
        if ((colorCode !== undefined) && (colorCode !== (DeviceColors.length - 1)))
            color = DeviceColors[colorCode][1];
    }

    const hasRunPermission = gw.gateway.currentUser.permissions.scenario.run;
    const [runClickActive, setRunClickActive] = useState(true);
    const registerScenarioReport = gw.gateway.scenarioReports.register;
    const runClickH = useCallback(() => {
        if (runClickActive) {
            registerScenarioReport(scenario);
            scenario.run();
            setTimeout(() => {
                try {
                    setRunClickActive(true);
                }
                catch (err) {
                    console.log(err);
                }
            }, 1500 * 3 + 100);
            setRunClickActive(false);
        }
    }, [scenario, registerScenarioReport, runClickActive])

    return (
        <div className="device-button scenario-switch-button" style={{ width: 54 }}>
            {/* set width > خاموش, for better placement of buttons and not changing on state changes*/}
            <ScenarioSwitchIcon color={color} shape={shape} className="scenario-button-icon" />
            <div className="scenario-label" style={{ color: color }}>
                {label}
            </div>
            {(hasRunPermission && isValid) ?
                <div className="device-on-click-overlay" onClick={runClickH} /> : null}
        </div>
    );
}

function RemoteButtons({
    device, subDeviceSelectionCallback, dontOpenPage,
    selectedDevices, showSubDevices }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    const hs = useContext(HistoryStackContext);

    if (!showSubDevices) {
        return <div
            className="remote-sub-devices"
            style={{ gridAutoFlow: "column" }}>
            <div className="remote-sub-device">
                <IconRemote className="icon" />
                {/* <div className="name"></div> */}
            </div>
        </div>
    } else {
        let subDevices = [];
        if (device.sub_devices) {
            subDevices = device.sub_devices.filter(
                d => supportedRemoteDeviceTypes.includes(d.type));
        }
        return (
            <div
                className="remote-sub-devices"
                style={
                    (subDevices.length < 4) ?
                        { gridAutoFlow: "column" } : { gridTemplateColumns: "repeat(4, auto)" }
                }
                dir={l.isRTL ? "rtl" : "ltr"}>
                {
                    (subDevices.length > 0) ? subDevices.map(subDevice => (
                        <RemoteSubDevice
                            subDevice={subDevice}
                            onClick={() => {
                                if (subDeviceSelectionCallback) {
                                    subDeviceSelectionCallback(subDevice);
                                } else {
                                    if (dontOpenPage !== true) {
                                        const parentDevice = device;
                                        hs.push("/devices/detail/remote-control", {
                                            parentDeviceMac: parentDevice.mac,
                                            subDeviceMac: subDevice.mac,
                                        });
                                    }
                                }
                            }}
                            isSelected={selectedDevices.includes(subDevice.mac)}
                            key={"remote-sub-device-mac-" + subDevice.mac} />
                    )) : <div className="no-remote-added">
                        {s.noVirtualDeviceInRemote}
                    </div>
                }
            </div>
        )
    }
}

function RemoteSubDevice({ onClick, subDevice, isSelected }) {
    let iconSvg = undefined;
    switch (subDevice.type) {
        case DeviceType.tvRemote:
            iconSvg = IconRemoteTv;
            break;
        case DeviceType.receiverRemote:
            iconSvg = IconRemoteReciever;
            break;
        case DeviceType.fanRemote:
            iconSvg = IconRemoteFan;
            break;
        case DeviceType.splitRemote:
            iconSvg = IconRemoteAc;
            break;
        case DeviceType.soundBarRemote:
            iconSvg = IconRemoteSoundbar;
            break;
        case DeviceType.projectorRemote:
            iconSvg = IconRemoteProjector;
            break;
        case DeviceType.homeTheatreRemote:
        default:
            iconSvg = IconRemoteHomeTheater;
            break;
    }

    let name = subDevice.name;
    if (isSelected) {
        if (name.length > 12) {
            name = name.substring(0, 12 - 2) + "…"
        }
    }

    return (
        <div className="remote-sub-device" onClick={onClick}>
            <div className={isSelected ? "remote-selection-panel active" : "remote-selection-panel"} />
            {React.createElement(iconSvg, { className: "icon" })}
            <div className="name">
                {name}
            </div>
        </div>
    )
}

function SymbolicCurtainButton() {
    return <div className="device-button">
        <IconCurtainSymbol />
        <div className="status-placeholder" />
    </div>
}

export function CurtainSingleButton({ onClickH, showStates, channel, open }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    let deviceIcon = null;
    const labelText = open ? s.curtain.open : s.curtain.close;

    switch (channel) {
        case 2:
            if (open) {
                deviceIcon = IconCurtainOpen2;
            } else {
                deviceIcon = IconCurtainClose2;
            }
            break;
        case 1:
        default:
            if (open) {
                deviceIcon = IconCurtainOpen1;
            } else {
                deviceIcon = IconCurtainClose1;
            }
            break;
    }

    if (!showStates) {
        return (
            <div className="device-button"
                style={{ width: 65 }}> {/* set width > label's length, for better placement of buttons and not changing on state changes*/}
                {React.createElement(deviceIcon, { className: "device-button-icon-on" })}
                <div className="status-placeholder" />
            </div>
        );
    } else {
        return (
            <div className="device-button"
                style={{ width: 65 }}> {/* set width > label's length, for better placement of buttons and not changing on state changes*/}
                {React.createElement(deviceIcon, { className: "device-button-icon-on" })}
                <div className="device-curtain-state-label status-text-or-icon">{labelText}</div>
                <div className="device-on-click-overlay" onClick={onClickH} />
            </div>
        );
    }
}

export function CurtainStatePanel({ onClickH, side, channel, state, waitingForChangeState, notShowInfoSection }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;

    if ((state === CurtainState.idle) || (state === undefined)) {
        return null;
    }

    let deviceIcon = null;
    const labelText = (state === CurtainState.open) ? s.curtain.opening : s.curtain.closing;

    const shouldShowWaiting = waitingForChangeState && !waitingForChangeState.ignoreWaiting;

    switch (channel) {
        case 2:
            if (state === CurtainState.open) {
                deviceIcon = IconCurtainOpenDark2;
            } else {
                deviceIcon = IconCurtainCloseDark2;
            } // what about idle and undefined? there is another `return` up there.
            break;
        case 1:
        default:
            if (state === CurtainState.open) {
                deviceIcon = IconCurtainOpenDark1;
            } else {
                deviceIcon = IconCurtainCloseDark1;
            }
            break;
    }

    return (
        <div className={"curtain-panel " + side} onClick={shouldShowWaiting ? undefined : onClickH}>
            {React.createElement(deviceIcon, { className: "icon" })}
            <div className="label">{labelText}</div>

            {
                notShowInfoSection ? null : (
                    !shouldShowWaiting ?
                        <div className="info">{s.curtain.tapToStop}</div>
                        : <IconThreeDotsLoadingDark className="curtain-dots-icon" />
                )
            }
        </div>
    );
}